<script setup lang="ts">
    import { onClickOutside, useFocusWithin } from '@vueuse/core';

    type HubSwitcherHub = AImageCardComponentStoryblok & {
        url?: string;
    };

    const {
        hubs = [],
        isMobile = false,
        isHubSwitcherOpen: initialIsHubSwitcherOpen = false
    } = defineProps<{
        hubs?: HubSwitcherHub[];
        isMobile?: boolean;
        isHubSwitcherOpen?: boolean;
    }>();

    const emit = defineEmits(['update:isHubSwitcherOpen']);

    const { t } = useI18n();
    const { context } = usePlatformMeta();
    const currentPlatformLabel = usePlatformLabel(context);

    const hubSwitcherModalController = useModal();
    const isHubSwitcherOpen = ref(initialIsHubSwitcherOpen);
    const { clampBody, releaseBody } = useFixedBody();

    const openModal = () => {
        hubSwitcherModalController.open();
        isHubSwitcherOpen.value = true;
        clampBody();
        emit('update:isHubSwitcherOpen', isHubSwitcherOpen.value);
    };

    const closeModal = () => {
        hubSwitcherModalController.close();
        isHubSwitcherOpen.value = false;
        releaseBody();
    };

    const closeModalWithEmit = () => {
        closeModal();
        emit('update:isHubSwitcherOpen', isHubSwitcherOpen.value);
    };

    const processedHubs = computed(() => {
        return hubs.map((blok) => ({
            ...blok,
            url: useStoryblokUrl(blok.link)
        }));
    });

    const modalContentElement = ref();
    onClickOutside(modalContentElement, () => closeModal());

    const { focused } = useFocusWithin(modalContentElement);

    watch(focused, (focused) => {
        if (!document.hasFocus()) return;

        if (!focused && isHubSwitcherOpen.value) {
            closeModal();
        }
    });

    const switchButton = ref();
    const closeButtonRef = ref();

    watch(closeButtonRef, () => {
        if (hubSwitcherModalController.isOpenWithFocus.value && hubSwitcherModalController.isOpen.value) {
            if (closeButtonRef.value) {
                closeButtonRef.value.focus();
            }
        }

        if (!hubSwitcherModalController.isOpenWithFocus.value) {
            if (switchButton.value) {
                switchButton.value.focus({ preventScroll: true });
            }
        }
    });

    onUnmounted(() => {
        if (isHubSwitcherOpen.value) {
            releaseBody();
        }
    });
</script>

<template>
    <button
        ref="switchButton"
        class="portal-switcher"
        :class="isMobile ? '--is-mobile' : ''"
        @click="openModal"
        @keydown.space="hubSwitcherModalController.openWithFocus"
    >
        <span class="sr-only">{{ t('platform.current') }}</span>
        <span aria-hidden="true">{{ t('platform.platform') }}:</span>
        <div aria-hidden="true">
            {{ currentPlatformLabel }}
            <img
                src="~/assets/icons/chevron-right.svg"
                alt="chevron"
                aria-hidden="true"
            />
        </div>
    </button>

    <Teleport to="body">
        <Transition name="modal">
            <div
                v-if="hubSwitcherModalController.isOpen.value"
                class="hub-modal-wrapper"
            >
                <div
                    ref="modalContentElement"
                    class="hub-modal"
                >
                    <div class="header">
                        <h2>{{ t('platform.headline') }}</h2>

                        <SharedCloseButton
                            id="hub-switcher-close"
                            ref="closeButtonRef"
                            :label="t('general.close')"
                            :icon-inverted="true"
                            @click="closeModal"
                            @keydown.space="hubSwitcherModalController.closeWithFocus"
                        />
                    </div>

                    <div
                        class="hubs"
                        :class="'--grid-' + hubs.length"
                    >
                        <StoryblokComponentWrapper
                            v-for="blok in processedHubs"
                            :key="blok._uid"
                            :blok="blok"
                            @click="closeModalWithEmit"
                        />
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
    @import '~/assets/scss/navigation/header-hub-switcher.scss';
</style>
